import { useMutation } from "@tanstack/react-query";
import { api } from "./api";

// Mirrors apps/api/src/lib/import/sales-import.ts's shapes 1:1 (raw Zod-free
// objects, same convention as leads-import.ts). TASK-11.

export interface SalesMapping {
  email: string | null;
  value: string | null;
  event_date: string | null;
  currency: string | null;
  order_id: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  cash_in: string | null;
}

export const EMPTY_SALES_MAPPING: SalesMapping = {
  email: null,
  value: null,
  event_date: null,
  currency: null,
  order_id: null,
  phone: null,
  first_name: null,
  last_name: null,
  cash_in: null,
};

export type SalesRowStatus = "new" | "already_imported" | "duplicate" | "error";
export interface SalesPreviewRow {
  row: number;
  email: string | null;
  value: number | null;
  currency: string | null;
  order_id: string | null;
  cash_in: number | null;
  event_date: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  valid: boolean;
  reason?:
    | "missing_email"
    | "invalid_email"
    | "missing_value"
    | "invalid_value"
    | "missing_event_date"
    | "invalid_event_date";
  status: SalesRowStatus;
}

export interface SalesPreviewResult {
  headers: string[];
  delimiter: "," | ";";
  mappingValid: boolean;
  counts: { new: number; already_imported: number; duplicates: number; errors: number };
  total: number;
  sample: SalesPreviewRow[];
}

export function useSalesPreview(projectId: number) {
  return useMutation({
    mutationFn: (input: {
      csv: string;
      mapping: SalesMapping;
      eventType?: string;
      statusFilter?: SalesRowStatus;
    }) =>
      api<SalesPreviewResult>(`/admin/projects/${projectId}/import/preview`, {
        method: "POST",
        body: JSON.stringify({ ...input, type: "sales" }),
      }),
  });
}

export interface CommitSalesResponse {
  ok: true;
  batch_id: number;
  imported: number;
  skipped_duplicates: number;
  errors: number;
  total: number;
}

export function useCommitSalesImport(projectId: number) {
  return useMutation({
    mutationFn: (input: {
      csv: string;
      mapping: SalesMapping;
      legalBasisConfirmed: boolean;
      label?: string;
      eventType?: string;
      sendToMeta?: boolean;
    }) =>
      api<CommitSalesResponse>(`/admin/projects/${projectId}/import/commit`, {
        method: "POST",
        body: JSON.stringify({ ...input, type: "sales" }),
      }),
  });
}
